<template>
  <div class="profile-option pr-2">
    <tooltip
      ref="profileTooltip"
      placement="top-start"
      trigger="click"
      hide-on-click="toggle"
      :arrow="false"
      sticky="reference"
      max-width="450"
      theme="none"
      interactive
      :popper-options="popperOptions"
    >
      <template #default>
        <div
          class="flex cursor-pointer items-center rounded-full hover:bg-white/10"
        >
          <div class="modal-icon bg-white/10">
            <div class="flex h-full items-center justify-center">
              <icon-base :icon="IconProfileSmall" view-box="-1.25 0 16 16" />
            </div>
          </div>
          <div class="px-2">
            <icon-base
              :icon="IconLineArrowRight"
              width="12"
              height="12"
              class="rotate-90 transform"
            />
          </div>
        </div>
      </template>
      <template #content>
        <div class="communicate-from w-78 rounded bg-white shadow-popover">
          <div class="flex items-center gap-x-2 px-4 py-2">
            <icon-base
              class="shrink-0 place-self-center"
              height="40"
              width="40"
              view-box="0 0 48 48"
              :icon="IconProfile"
            />
            <div class="grow overflow-hidden text-sm whitespace-nowrap">
              <div
                class="flex shrink flex-col justify-between space-y-1 font-medium"
              >
                <span v-tooltip="fullUserName" class="truncate text-headline">
                  {{ fullUserName }}
                </span>
                <span
                  v-tooltip="{
                    content: userRoles,
                    onShow: () => !!user.roles.length,
                  }"
                  class="truncate text-xs font-normal"
                >
                  {{ userRoles }}
                </span>
              </div>
            </div>
          </div>
          <communication-profile-numbers />
        </div>
      </template>
    </tooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import startCase from "lodash/startCase";

import { formatUserFullName, getUserRoles } from "@/helpers/formatting";

import CommunicationProfileNumbers from "@/components/communication/CommunicationProfileNumbers.vue";
import IconLineArrowRight from "@/components/icons/IconLineArrowRight.vue";
import IconProfileSmall from "@/components/icons/IconProfileSmall.vue";
import IconProfile from "@/components/icons/sidebar/IconProfile.vue";

import type { IUser } from "@/models/users";
import type { TippyComponent } from "vue-tippy";

const { getters } = useStore();

const user = computed<IUser>(() => getters["auth/user"]);

const profileTooltip = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["bottom", "top"] },
    },
  ],
});

const fullUserName = computed(() => formatUserFullName(user.value));
const userRoles = computed(() =>
  startCase(getUserRoles(user.value?.roles)?.join(", "))
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.profile-option {
  @apply relative;
  &::after {
    content: "";
    @apply absolute -top-2 right-0 block h-[50px] border-r border-r-white/10 pl-2;
  }
}

.communicate-from {
  :deep() {
    .flex svg {
      @apply my-auto flex;
    }
  }
}
</style>
